/**
 * Service pour gérer les opérations liées aux brouillons de posts
 */

import { auth } from '../config/firebase';

const API_BASE_URL = 'https://api.socialness.ai';

export interface DraftPayload {
  brandId: string;
  title?: string;
  content: string;
  format: 'regular' | 'reel';
  platforms: string[];
  status?: 'draft';
  needsReview?: boolean;
  reviewerId?: string;
  media?: {
    url: string;
    type: 'image' | 'video' | 'IMAGE' | 'VIDEO';
    thumbnailUrl?: string;
    uploadedAt?: string;
    description?: string;
  };
  hashtags?: string[];
  tags?: string[];
  platformSettings?: Record<string, any>;
}

/**
 * Sauvegarde un brouillon en utilisant l'API management/posts
 * 
 * @param payload - Les données du brouillon à sauvegarder
 * @returns Le résultat de l'API ou un objet d'erreur
 */
export const saveDraft = async (payload: DraftPayload): Promise<any> => {
  try {
    // Vérifier si l'utilisateur est authentifié
    const user = auth.currentUser;
    if (!user) {
      throw new Error('Vous devez être connecté pour sauvegarder un brouillon');
    }

    const token = await user.getIdToken();

    // Préparer le payload selon le nouveau format API
    const apiPayload = {
      brandId: payload.brandId,
      title: payload.title || '',
      content: payload.content,
      format: payload.format,
      status: 'draft',
      needsReview: payload.needsReview || false,
      reviewerId: payload.reviewerId || '',
      platforms: payload.platforms,
      media: payload.media ? [{
        url: payload.media.url,
        type: payload.media.type.toUpperCase(), // Ensure uppercase for API
        thumbnailUrl: payload.media.thumbnailUrl || '',
        description: payload.media.description || ''
      }] : undefined,
      tags: payload.tags || [],
      platformSettings: payload.platformSettings || {}
    };

    console.log('Saving draft with new API format:', apiPayload);

    const response = await fetch(`${API_BASE_URL}/management/posts`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(apiPayload)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log('Draft saved successfully:', result);
    
    // Sauvegarder également en local comme backup
    saveLocalDraft(payload);
    
    return result;
  } catch (error) {
    console.error('Error saving draft with API:', error);
    
    // Utiliser le localStorage comme fallback en cas d'échec de l'API
    saveLocalDraft(payload);
    
    throw error;
  }
};

/**
 * Sauvegarde un brouillon localement dans localStorage
 * 
 * @param payload - Les données du brouillon à sauvegarder localement
 */
export const saveLocalDraft = (payload: DraftPayload): void => {
  try {
    const localDraft = {
      ...payload,
      savedAt: new Date().toISOString(),
      isDraft: true
    };
    
    const existingDrafts = JSON.parse(localStorage.getItem('contentDrafts') || '[]');
    existingDrafts.push(localDraft);
    localStorage.setItem('contentDrafts', JSON.stringify(existingDrafts));
    
    console.log('Draft saved locally:', localDraft);
  } catch (error) {
    console.error('Error saving draft locally:', error);
  }
};

/**
 * Récupère les brouillons sauvegardés localement
 * 
 * @returns Un tableau des brouillons sauvegardés localement
 */
export const getLocalDrafts = (): any[] => {
  try {
    const drafts = JSON.parse(localStorage.getItem('contentDrafts') || '[]');
    return drafts;
  } catch (error) {
    console.error('Error getting local drafts:', error);
    return [];
  }
}; 